import type { FlagsType } from '@iheartjane/feature-flags';
import { useEffect, useRef } from 'react';

import {
  registerJdid,
  registerMixpanelIdentity,
  registerSuperPropertiesStore,
  resetMixpanelIdentity,
} from '@jane/bloom/services/tracking/mixpanel';

type UseMixpanelRegistrationParams = {
  buildVersion: string;
  featureFlags?: FlagsType;
  isLoggedIn: boolean;
  jdid?: string;
  jdidIdentityEnabled: boolean;
  operatorId: number;
  operatorName: string;
  pluginVersion?: string;
  projectToken?: string | null;
  storeCity?: string;
  storeId?: number;
  storeName?: string;
  storeState?: string;
  uniqueId: string;
};

/**
 * Registers user identity and super properties with Mixpanel.
 *
 * Identity (`mixpanel.identify` + the full super-property payload) is
 * registered once per uniqueId. The pinned store can still change without
 * an identity change — e.g. URL-driven store overrides (`/store/:id/...`),
 * custom slug routes, or cookie/loader-data updates that bypass the
 * StoreSwitcher — so we additionally re-register the store-related super
 * properties whenever `storeId` changes. Without this, events fire with the
 * previously-registered (often stale, sometimes from a prior session via
 * localStorage persistence) storeId super prop.
 *
 * On logout (flag on), Mixpanel is reset before re-identifying with `jdid`.
 */
export function useMixpanelRegistration({
  isLoggedIn,
  jdid,
  jdidIdentityEnabled,
  projectToken,
  storeCity,
  storeId,
  storeName,
  storeState,
  uniqueId,
  ...rest
}: UseMixpanelRegistrationParams) {
  const registeredUniqueIdRef = useRef<string | null>(null);
  const registeredStoreIdRef = useRef<number | null>(null);
  const wasLoggedInRef = useRef(false);

  useEffect(() => {
    if (!projectToken || !storeCity || !storeId || !storeName || !storeState) {
      return;
    }

    if (uniqueId !== registeredUniqueIdRef.current) {
      const didLogout =
        jdidIdentityEnabled && wasLoggedInRef.current && !isLoggedIn;
      if (didLogout) {
        resetMixpanelIdentity();
      }

      registeredUniqueIdRef.current = uniqueId;
      registeredStoreIdRef.current = storeId;
      wasLoggedInRef.current = isLoggedIn;

      registerMixpanelIdentity({
        storeCity,
        storeId,
        storeName,
        storeState,
        uniqueId,
        ...rest,
      });

      // reset() clears super properties, so restore the jdid super property.
      if (didLogout && jdid) {
        registerJdid(jdid);
      }
    } else if (storeId !== registeredStoreIdRef.current) {
      registeredStoreIdRef.current = storeId;
      registerSuperPropertiesStore({
        storeCity,
        storeId,
        storeName,
        storeState,
      });
    }
  }, [
    isLoggedIn,
    jdid,
    jdidIdentityEnabled,
    projectToken,
    rest,
    storeCity,
    storeId,
    storeName,
    storeState,
    uniqueId,
  ]);
}
